import { create } from "zustand";
import { REPORT_STEPS } from "../../constants/reportBuilder.js";
import { emptyRuleGroup } from "../services/queryBuilderConfig.js";
import { METRIC_PRESETS } from "../../constants/reportBuilder.js";
import { createCalculatedField, createFieldLogic, createFilterRow } from "../services/reportBuilderSummary.js";
import { createMetricFromPreset, createMetricRow } from "../services/reportBuilderMetrics.js";

const initialStep = REPORT_STEPS[0].id;

export const useReportBuilderStore = create((set) => ({
  step: initialStep,
  datasetId: "vehicles",
  datasets: [],
  datasetsLoading: true,
  activeDataset: null,
  filterLogic: "and",
  filters: [],
  session: { startQuery: emptyRuleGroup(), endQuery: emptyRuleGroup() },
  calculatedFields: [],
  groupBy: [],
  metrics: [createMetricRow()],
  visualization: "table",
  reportName: "",
  estimate: null,
  estimateLoading: false,
  saving: false,
  saveSuccess: false,
  saveError: null,

  setStep: (step) => set({ step, saveSuccess: false, saveError: null }),
  setDatasetId: (datasetId) =>
    set((s) => ({
      datasetId,
      groupBy: [],
      filters: s.filters.map((f) => ({ ...f, field: "" })),
      metrics: s.metrics.map((m) => ({ ...m, field: m.sourceType === "field" ? "" : m.field })),
    })),
  setSessionStartQuery: (startQuery) => set((s) => ({ session: { ...s.session, startQuery } })),
  setSessionEndQuery: (endQuery) => set((s) => ({ session: { ...s.session, endQuery } })),
  setVisualization: (visualization) => set({ visualization }),
  setReportName: (reportName) => set({ reportName, saveError: null }),

  setFilterLogic: (filterLogic) => set({ filterLogic }),
  addFilter: () => set((s) => ({ filters: [...s.filters, createFilterRow()] })),
  updateFilter: (id, patch) =>
    set((s) => ({ filters: s.filters.map((f) => (f.id === id ? { ...f, ...patch } : f)) })),
  removeFilter: (id) => set((s) => ({ filters: s.filters.filter((f) => f.id !== id) })),

  addCalculatedField: () =>
    set((s) => ({ calculatedFields: [...s.calculatedFields, createCalculatedField()] })),
  updateCalculatedField: (id, patch) =>
    set((s) => ({
      calculatedFields: s.calculatedFields.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    })),
  removeCalculatedField: (id) =>
    set((s) => ({ calculatedFields: s.calculatedFields.filter((f) => f.id !== id) })),

  addFieldLogic: (fieldId) =>
    set((s) => ({
      calculatedFields: s.calculatedFields.map((f) =>
        f.id === fieldId ? { ...f, logics: [...(f.logics ?? []), createFieldLogic()] } : f,
      ),
    })),
  updateFieldLogic: (fieldId, logicId, patch) =>
    set((s) => ({
      calculatedFields: s.calculatedFields.map((f) =>
        f.id !== fieldId
          ? f
          : {
              ...f,
              logics: (f.logics ?? []).map((l) => (l.id === logicId ? { ...l, ...patch } : l)),
            },
      ),
    })),
  removeFieldLogic: (fieldId, logicId) =>
    set((s) => ({
      calculatedFields: s.calculatedFields.map((f) =>
        f.id !== fieldId
          ? f
          : {
              ...f,
              logics: (f.logics ?? []).length > 1 ? (f.logics ?? []).filter((l) => l.id !== logicId) : f.logics,
            },
      ),
    })),

  toggleGroupBy: (field) =>
    set((s) => ({
      groupBy: s.groupBy.includes(field) ? s.groupBy.filter((x) => x !== field) : [...s.groupBy, field],
    })),

  addMetric: () => set((s) => ({ metrics: [...s.metrics, createMetricRow()] })),
  addMetricFromPreset: (presetId, fieldNames = []) => {
    const preset = METRIC_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    set((s) => ({ metrics: [...s.metrics, createMetricFromPreset(preset, fieldNames)] }));
  },
  cloneMetric: (id) =>
    set((s) => {
      const source = s.metrics.find((m) => m.id === id);
      if (!source) return s;
      const copy = { ...source, id: createMetricRow().id, alias: source.alias ? `${source.alias}_copy` : "" };
      const idx = s.metrics.findIndex((m) => m.id === id);
      const metrics = [...s.metrics];
      metrics.splice(idx + 1, 0, copy);
      return { metrics };
    }),
  moveMetric: (id, direction) =>
    set((s) => {
      const idx = s.metrics.findIndex((m) => m.id === id);
      const target = idx + direction;
      if (idx < 0 || target < 0 || target >= s.metrics.length) return s;
      const metrics = [...s.metrics];
      const [item] = metrics.splice(idx, 1);
      metrics.splice(target, 0, item);
      return { metrics };
    }),
  updateMetric: (id, patch) =>
    set((s) => ({ metrics: s.metrics.map((m) => (m.id === id ? { ...m, ...patch } : m)) })),
  removeMetric: (id) =>
    set((s) => ({ metrics: s.metrics.length > 1 ? s.metrics.filter((m) => m.id !== id) : s.metrics })),

  setEstimate: (estimate) => set({ estimate, estimateLoading: false }),
  setEstimateLoading: (estimateLoading) => set({ estimateLoading }),
  setSaving: (saving) => set({ saving }),
  setSaveSuccess: () => set({ saving: false, saveSuccess: true, saveError: null }),
  setSaveError: (saveError) => set({ saving: false, saveError }),

  resetSaveState: () => set({ saveSuccess: false, saveError: null }),
}));
